import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, FileText, Edit, Copy, DoorOpen, Home, TreeDeciduous, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { projectsApi, roomsApi, exteriorApi, deckApi, cabinetsApi } from "@/lib/api";
import {
  formatCurrency, getDefaultRoomItems, getDefaultExteriorItems, getDefaultDeckItems,
  getDefaultCabinetCoreItems, getDefaultCabinetItems, ITEM_LABELS, PROJECT_TYPES,
  PREP_LEVELS, CABINET_GRADE_TEMPLATES,
} from "@/lib/utils";

export default function ProjectEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [createExteriorOpen, setCreateExteriorOpen] = useState(false);
  const [createDeckOpen, setCreateDeckOpen] = useState(false);
  const [createCabinetOpen, setCreateCabinetOpen] = useState(false);
  const [deleteRoomId, setDeleteRoomId] = useState(null);
  const [deleteExteriorId, setDeleteExteriorId] = useState(null);
  const [deleteDeckId, setDeleteDeckId] = useState(null);
  const [deleteCabinetId, setDeleteCabinetId] = useState(null);
  const [editingProject, setEditingProject] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [newRoom, setNewRoom] = useState({ name: "", length: 12, width: 10, height: 8 });
  const [newExterior, setNewExterior] = useState({ name: "Front", length: 40, height: 10 });
  const [newDeck, setNewDeck] = useState({ name: "Main Deck", length: 20, width: 12 });
  const [newCabinet, setNewCabinet] = useState({ name: "Kitchen Cabinets", pricing_mode: "linear_foot", finish_grade: "paint_grade" });
  const [bulkPrepLevel, setBulkPrepLevel] = useState("3");
  const [bulkCoats, setBulkCoats] = useState("2");
  const [bulkApplying, setBulkApplying] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadProject(); }, [projectId]);

  const loadProject = async () => {
    try {
      const [projectRes, estimateRes] = await Promise.all([
        projectsApi.getById(projectId),
        projectsApi.getEstimate(projectId).catch(() => null)
      ]);
      setProject(projectRes.data);
      if (estimateRes) setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      await projectsApi.update(projectId, {
        name: project.name, client_name: project.client_name, client_address: project.client_address,
        client_phone: project.client_phone, client_email: project.client_email, project_type: project.project_type,
        labor_rate: project.labor_rate, material_cost_mode: project.material_cost_mode, notes: project.notes,
      });
      toast.success("Project saved");
      setEditingProject(false);
      loadProject();
    } catch (error) {
      toast.error("Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkApply = async () => {
    if (!project.rooms?.length) return;
    setBulkApplying(true);
    try {
      await Promise.all(project.rooms.map(room => {
        const items = room.items.map(item => item.enabled
          ? { ...item, prep_level: parseInt(bulkPrepLevel), coats: parseInt(bulkCoats) }
          : item);
        return roomsApi.update(projectId, room.id, { items });
      }));
      toast.success(`Applied Level ${bulkPrepLevel} prep / ${bulkCoats} coats to all included items in every room`);
      loadProject();
    } catch (error) {
      toast.error("Failed to bulk-apply");
    } finally {
      setBulkApplying(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoom.name.trim()) { toast.error("Room name is required"); return; }
    try {
      const roomData = { ...newRoom, items: getDefaultRoomItems(newRoom.length, newRoom.width, newRoom.height) };
      const response = await roomsApi.add(projectId, roomData);
      setProject(response.data);
      setCreateRoomOpen(false);
      setNewRoom({ name: "", length: 12, width: 10, height: 8 });
      toast.success("Room added");
      const newRoomId = response.data.rooms[response.data.rooms.length - 1].id;
      navigate(`/project/${projectId}/room/${newRoomId}`);
    } catch (error) {
      toast.error("Failed to create room");
    }
  };

  const handleCreateExterior = async () => {
    if (!newExterior.name.trim()) { toast.error("Side name is required"); return; }
    try {
      const sideData = { ...newExterior, items: getDefaultExteriorItems(newExterior.length, newExterior.height) };
      const response = await exteriorApi.add(projectId, sideData);
      setProject(response.data);
      setCreateExteriorOpen(false);
      setNewExterior({ name: "Front", length: 40, height: 10 });
      toast.success("Exterior side added");
      const newSideId = response.data.exterior_sides[response.data.exterior_sides.length - 1].id;
      navigate(`/project/${projectId}/exterior/${newSideId}`);
    } catch (error) {
      toast.error("Failed to create exterior side");
    }
  };

  const handleCreateDeck = async () => {
    if (!newDeck.name.trim()) { toast.error("Deck name is required"); return; }
    try {
      const deckData = { ...newDeck, items: getDefaultDeckItems(newDeck.length, newDeck.width) };
      const response = await deckApi.add(projectId, deckData);
      setProject(response.data);
      setCreateDeckOpen(false);
      setNewDeck({ name: "Main Deck", length: 20, width: 12 });
      toast.success("Deck area added");
      const newDeckId = response.data.deck_areas[response.data.deck_areas.length - 1].id;
      navigate(`/project/${projectId}/deck/${newDeckId}`);
    } catch (error) {
      toast.error("Failed to create deck area");
    }
  };

  const handleCreateCabinet = async () => {
    if (!newCabinet.name.trim()) { toast.error("Cabinet area name is required"); return; }
    try {
      const cabinetData = {
        ...newCabinet,
        core_items: getDefaultCabinetCoreItems(newCabinet.pricing_mode),
        items: getDefaultCabinetItems(newCabinet.finish_grade),
      };
      const response = await cabinetsApi.add(projectId, cabinetData);
      setProject(response.data);
      setCreateCabinetOpen(false);
      setNewCabinet({ name: "Kitchen Cabinets", pricing_mode: "linear_foot", finish_grade: "paint_grade" });
      toast.success("Cabinet area added");
      const newCabinetId = response.data.cabinet_areas[response.data.cabinet_areas.length - 1].id;
      navigate(`/project/${projectId}/cabinet/${newCabinetId}`);
    } catch (error) {
      toast.error("Failed to create cabinet area");
    }
  };

  const handleDeleteRoom = async () => { if (!deleteRoomId) return; try { await roomsApi.delete(projectId, deleteRoomId); toast.success("Room deleted"); loadProject(); } catch (error) { toast.error("Failed"); } finally { setDeleteRoomId(null); } };
  const handleDeleteExterior = async () => { if (!deleteExteriorId) return; try { await exteriorApi.delete(projectId, deleteExteriorId); toast.success("Exterior deleted"); loadProject(); } catch (error) { toast.error("Failed"); } finally { setDeleteExteriorId(null); } };
  const handleDeleteDeck = async () => { if (!deleteDeckId) return; try { await deckApi.delete(projectId, deleteDeckId); toast.success("Deck deleted"); loadProject(); } catch (error) { toast.error("Failed"); } finally { setDeleteDeckId(null); } };
  const handleDeleteCabinet = async () => { if (!deleteCabinetId) return; try { await cabinetsApi.delete(projectId, deleteCabinetId); toast.success("Cabinet area deleted"); loadProject(); } catch (error) { toast.error("Failed"); } finally { setDeleteCabinetId(null); } };
  const handleCloneRoom = async (roomId) => { try { await roomsApi.clone(projectId, roomId); toast.success("Room cloned"); loadProject(); } catch (error) { toast.error("Failed"); } };
  const handleCloneProject = async () => { try { const response = await projectsApi.clone(projectId); toast.success("Project cloned"); navigate(`/project/${response.data.id}`); } catch (error) { toast.error("Failed"); } };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!project) return null;

  const type = project.project_type || "interior";
  const showInterior = ["interior", "both", "all"].includes(type);
  const showExterior = ["exterior", "both", "all"].includes(type);
  const showDeck = ["deck", "all"].includes(type);
  const showCabinets = ["cabinets", "all"].includes(type);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">{project.name}</h1>
              <p className="text-xs text-muted-foreground">{project.client_name || "No client"} • {PROJECT_TYPES.find(t => t.value === type)?.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCloneProject}><Copy className="w-4 h-4 mr-1" />Clone</Button>
            <Button variant="outline" onClick={() => setEditingProject(true)}><Edit className="w-4 h-4 mr-2" />Edit</Button>
            <Button className="btn-primary" onClick={() => navigate(`/project/${projectId}/summary`)}><FileText className="w-4 h-4 mr-2" />Summary</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue={showInterior ? "interior" : showExterior ? "exterior" : showDeck ? "deck" : "cabinets"}>
              <TabsList className="mb-4">
                {showInterior && <TabsTrigger value="interior" className="gap-2"><DoorOpen className="w-4 h-4" />Interior</TabsTrigger>}
                {showExterior && <TabsTrigger value="exterior" className="gap-2"><Home className="w-4 h-4" />Exterior</TabsTrigger>}
                {showDeck && <TabsTrigger value="deck" className="gap-2"><TreeDeciduous className="w-4 h-4" />Deck</TabsTrigger>}
                {showCabinets && <TabsTrigger value="cabinets" className="gap-2"><Package className="w-4 h-4" />Cabinets</TabsTrigger>}
              </TabsList>

              {showInterior && (
                <TabsContent value="interior" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold">Rooms ({project.rooms?.length || 0})</h2>
                    <Dialog open={createRoomOpen} onOpenChange={setCreateRoomOpen}>
                      <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Add Room</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle className="font-heading">Add New Room</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="input-group"><Label className="input-label">Room Name *</Label><Input value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} placeholder="e.g., Living Room" /></div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="input-group"><Label className="input-label">Length</Label><Input type="number" value={newRoom.length} onChange={(e) => setNewRoom({ ...newRoom, length: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                            <div className="input-group"><Label className="input-label">Width</Label><Input type="number" value={newRoom.width} onChange={(e) => setNewRoom({ ...newRoom, width: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                            <div className="input-group"><Label className="input-label">Height</Label><Input type="number" value={newRoom.height} onChange={(e) => setNewRoom({ ...newRoom, height: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                          </div>
                          <p className="text-xs text-muted-foreground">Walls 9'+ height will use slower production rate</p>
                          <Button onClick={handleCreateRoom} className="btn-primary w-full">Add Room</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {project.rooms?.length === 0 ? (
                    <div className="empty-state"><DoorOpen className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="font-heading text-lg font-semibold mb-2">No Rooms Yet</h3><p className="text-muted-foreground mb-4">Add rooms to start</p></div>
                  ) : (
                    <div className="space-y-3">
                      {project.rooms.length > 1 && (
                        <Card className="bg-secondary/20">
                          <CardContent className="p-4 flex flex-wrap items-end gap-3">
                            <div className="input-group flex-1 min-w-32">
                              <Label className="input-label text-xs">Bulk-apply Prep Level</Label>
                              <Select value={bulkPrepLevel} onValueChange={setBulkPrepLevel}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{PREP_LEVELS.map(p => <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="input-group flex-1 min-w-28">
                              <Label className="input-label text-xs">Coats</Label>
                              <Select value={bulkCoats} onValueChange={setBulkCoats}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 Coat</SelectItem>
                                  <SelectItem value="2">2 Coats</SelectItem>
                                  <SelectItem value="3">3 Coats</SelectItem>
                                  <SelectItem value="4">4 Coats</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button variant="outline" onClick={handleBulkApply} disabled={bulkApplying}>
                              {bulkApplying ? "Applying..." : `Apply to all ${project.rooms.length} rooms`}
                            </Button>
                            <p className="text-xs text-muted-foreground w-full">Sets prep level and coats on every currently-included item in every room. Excluded items are untouched.</p>
                          </CardContent>
                        </Card>
                      )}
                      {project.rooms.map((room, idx) => (
                        <Card key={room.id} className="card-hover cursor-pointer group" onClick={() => navigate(`/project/${projectId}/room/${room.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center font-mono text-sm">{idx + 1}</div>
                                <div>
                                  <h3 className="font-heading font-semibold">{room.name}</h3>
                                  <p className="text-sm text-muted-foreground">{room.length}' × {room.width}' × {room.height}' {room.height > 9 && <span className="text-amber-500">(High)</span>}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleCloneRoom(room.id); }}><Copy className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setDeleteRoomId(room.id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {showExterior && (
                <TabsContent value="exterior" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold">Exterior Sides ({project.exterior_sides?.length || 0})</h2>
                    <Dialog open={createExteriorOpen} onOpenChange={setCreateExteriorOpen}>
                      <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Add Side</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle className="font-heading">Add Exterior Side</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="input-group">
                            <Label className="input-label">Side Name</Label>
                            <Select value={newExterior.name} onValueChange={(v) => setNewExterior({ ...newExterior, name: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="Front">Front</SelectItem><SelectItem value="Back">Back</SelectItem><SelectItem value="Left Side">Left Side</SelectItem><SelectItem value="Right Side">Right Side</SelectItem><SelectItem value="Garage">Garage</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="input-group"><Label className="input-label">Length</Label><Input type="number" value={newExterior.length} onChange={(e) => setNewExterior({ ...newExterior, length: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                            <div className="input-group"><Label className="input-label">Height</Label><Input type="number" value={newExterior.height} onChange={(e) => setNewExterior({ ...newExterior, height: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                          </div>
                          <Button onClick={handleCreateExterior} className="btn-primary w-full">Add Side</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {project.exterior_sides?.length === 0 ? (
                    <div className="empty-state"><Home className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="font-heading text-lg font-semibold mb-2">No Exterior Sides</h3></div>
                  ) : (
                    <div className="space-y-3">
                      {project.exterior_sides.map((side, idx) => (
                        <Card key={side.id} className="card-hover cursor-pointer group" onClick={() => navigate(`/project/${projectId}/exterior/${side.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center font-mono text-sm text-amber-500">{idx + 1}</div>
                                <div><h3 className="font-heading font-semibold">{side.name}</h3><p className="text-sm text-muted-foreground">{side.length}' × {side.height}' = {(side.length * side.height).toFixed(0)} sq ft</p></div>
                              </div>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); setDeleteExteriorId(side.id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {showDeck && (
                <TabsContent value="deck" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold">Deck Areas ({project.deck_areas?.length || 0})</h2>
                    <Dialog open={createDeckOpen} onOpenChange={setCreateDeckOpen}>
                      <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Add Deck</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle className="font-heading">Add Deck Area</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="input-group"><Label className="input-label">Deck Name</Label><Input value={newDeck.name} onChange={(e) => setNewDeck({ ...newDeck, name: e.target.value })} placeholder="e.g., Main Deck, Front Porch" /></div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="input-group"><Label className="input-label">Length</Label><Input type="number" value={newDeck.length} onChange={(e) => setNewDeck({ ...newDeck, length: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                            <div className="input-group"><Label className="input-label">Width</Label><Input type="number" value={newDeck.width} onChange={(e) => setNewDeck({ ...newDeck, width: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
                          </div>
                          <Button onClick={handleCreateDeck} className="btn-primary w-full">Add Deck</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {project.deck_areas?.length === 0 ? (
                    <div className="empty-state"><TreeDeciduous className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="font-heading text-lg font-semibold mb-2">No Deck Areas</h3></div>
                  ) : (
                    <div className="space-y-3">
                      {project.deck_areas.map((deck, idx) => (
                        <Card key={deck.id} className="card-hover cursor-pointer group" onClick={() => navigate(`/project/${projectId}/deck/${deck.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center font-mono text-sm text-green-400">{idx + 1}</div>
                                <div><h3 className="font-heading font-semibold">{deck.name}</h3><p className="text-sm text-muted-foreground">{deck.length}' × {deck.width}' = {(deck.length * deck.width).toFixed(0)} sq ft</p></div>
                              </div>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); setDeleteDeckId(deck.id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              {showCabinets && (
                <TabsContent value="cabinets" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold">Cabinet Areas ({project.cabinet_areas?.length || 0})</h2>
                    <Dialog open={createCabinetOpen} onOpenChange={setCreateCabinetOpen}>
                      <DialogTrigger asChild><Button className="btn-primary"><Plus className="w-4 h-4 mr-2" />Add Cabinet Area</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle className="font-heading">Add Cabinet Area</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="input-group"><Label className="input-label">Area Name</Label><Input value={newCabinet.name} onChange={(e) => setNewCabinet({ ...newCabinet, name: e.target.value })} placeholder="e.g., Kitchen Cabinets, Bathroom Vanity" /></div>
                          <div className="input-group">
                            <Label className="input-label">Pricing Mode</Label>
                            <Select value={newCabinet.pricing_mode} onValueChange={(v) => setNewCabinet({ ...newCabinet, pricing_mode: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="linear_foot">Linear Foot</SelectItem>
                                <SelectItem value="per_unit">Per Unit (Door/Drawer/Box)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="input-group">
                            <Label className="input-label">Finish Grade</Label>
                            <Select value={newCabinet.finish_grade} onValueChange={(v) => setNewCabinet({ ...newCabinet, finish_grade: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>{CABINET_GRADE_TEMPLATES.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">Pre-enables the finish-process checklist items typical for this grade.</p>
                          </div>
                          <Button onClick={handleCreateCabinet} className="btn-primary w-full">Add Cabinet Area</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  {project.cabinet_areas?.length === 0 ? (
                    <div className="empty-state"><Package className="w-12 h-12 text-muted-foreground mb-4" /><h3 className="font-heading text-lg font-semibold mb-2">No Cabinet Areas</h3></div>
                  ) : (
                    <div className="space-y-3">
                      {project.cabinet_areas.map((cabinet, idx) => (
                        <Card key={cabinet.id} className="card-hover cursor-pointer group" onClick={() => navigate(`/project/${projectId}/cabinet/${cabinet.id}`)}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center font-mono text-sm text-purple-400">{idx + 1}</div>
                                <div>
                                  <h3 className="font-heading font-semibold">{cabinet.name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {cabinet.pricing_mode === "per_unit" ? "Per Unit" : "Linear Foot"} • {cabinet.finish_grade === "stain_grade" ? "Stain-Grade" : "Paint-Grade"}
                                  </p>
                                </div>
                              </div>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); setDeleteCabinetId(cabinet.id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>

          <div className="space-y-6">
            <Card className="sticky-panel">
              <CardHeader><CardTitle className="font-heading text-lg">Quick Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {estimate ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Hours</span><span className="font-mono">{estimate.total_labor_hours} hrs{estimate.nonproductive_hours > 0 ? ` (+${estimate.nonproductive_hours} nonprod.)` : ""}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Cost</span><span className="font-mono">{formatCurrency(estimate.total_labor_cost)}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Materials</span><span className="font-mono">{formatCurrency(estimate.total_material_cost)}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Overhead</span><span className="font-mono">{formatCurrency(estimate.overhead_amount)}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Profit</span><span className="font-mono">{formatCurrency(estimate.profit_amount)}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Incentive</span><span className="font-mono">{formatCurrency(estimate.incentive_amount)}</span></div>
                    </div>
                    <div className="border-t border-border pt-4"><div className="flex justify-between items-center"><span className="font-heading font-semibold">Total</span><span className="font-mono text-xl text-amber-500">{formatCurrency(estimate.client_total)}</span></div></div>
                    {estimate.payment_schedule?.length > 0 && (
                      <div className="border-t border-border pt-4 space-y-2">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Payment Schedule</span>
                        {estimate.payment_schedule.map(p => (
                          <div key={p.label} className="flex justify-between items-center text-sm"><span>{p.label}</span><span className="font-mono">{formatCurrency(p.amount)}</span></div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4"><p className="text-muted-foreground text-sm">Add items to see estimate</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Dialog open={editingProject} onOpenChange={setEditingProject}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Project</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="input-group"><Label className="input-label">Project Name</Label><Input value={project.name} onChange={(e) => setProject({ ...project, name: e.target.value })} /></div>
            <div className="input-group">
              <Label className="input-label">Project Type</Label>
              <Select value={project.project_type || "interior"} onValueChange={(v) => setProject({ ...project, project_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="input-group"><Label className="input-label">Client Name</Label><Input value={project.client_name} onChange={(e) => setProject({ ...project, client_name: e.target.value })} /></div>
            <div className="input-group"><Label className="input-label">Client Address</Label><Input value={project.client_address} onChange={(e) => setProject({ ...project, client_address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="input-group"><Label className="input-label">Phone</Label><Input value={project.client_phone} onChange={(e) => setProject({ ...project, client_phone: e.target.value })} /></div>
              <div className="input-group"><Label className="input-label">Email</Label><Input value={project.client_email} onChange={(e) => setProject({ ...project, client_email: e.target.value })} /></div>
            </div>
            <div className="input-group"><Label className="input-label">Labor Rate ($/hr)</Label><Input type="number" value={project.labor_rate} onChange={(e) => setProject({ ...project, labor_rate: parseFloat(e.target.value) || 0 })} className="font-mono" /></div>
            <div className="input-group">
              <Label className="input-label">Material Cost Mode</Label>
              <Select value={project.material_cost_mode || "gallons"} onValueChange={(v) => setProject({ ...project, material_cost_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gallons">By Gallons Used</SelectItem>
                  <SelectItem value="percent_of_labor">% of Labor Cost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="input-group"><Label className="input-label">Notes</Label><Textarea value={project.notes} onChange={(e) => setProject({ ...project, notes: e.target.value })} rows={3} /></div>
            <Button onClick={handleSaveProject} className="btn-primary w-full" disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRoomId} onOpenChange={() => setDeleteRoomId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Room?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteRoom} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
      <AlertDialog open={!!deleteExteriorId} onOpenChange={() => setDeleteExteriorId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Exterior?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteExterior} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
      <AlertDialog open={!!deleteDeckId} onOpenChange={() => setDeleteDeckId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Deck?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteDeck} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
      <AlertDialog open={!!deleteCabinetId} onOpenChange={() => setDeleteCabinetId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Cabinet Area?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteCabinet} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
}
